import C from "@/components/sidebar/StandardSidebar.module.css";

import { siteName } from "@/components/SiteName.tsx";
import { localeCompareBy } from "@/components/data/comparers.ts";
import { getFilteredItems } from "@/components/data/getFilteredItems.ts";
import { getSortedItems, type SimpleSortField } from "@/components/data/getSortedItems.ts";
import { MaskIcon } from "@/components/icon/MaskIcon.tsx";
import { ShortDate } from "@/components/l10n/DateFormat.tsx";
import { EmptyItem } from "@/components/sidebar/EmptyItem.tsx";
import { ListResult, SidebarList, type SidebarListState } from "@/components/sidebar/SidebarList.tsx";
import { StandardBlock } from "@/components/sidebar/StandardSidebar.tsx";
import { accessListQueryAtom } from "@/modules/access/accessListQuery.ts";
import type { AccessDelegationWithType } from "@/modules/access/delegation/accessDelegationApi.ts";
import { delegationLink, tokenLink } from "@/modules/access/links.ts";
import { scopeLabel } from "@/modules/access/scopeLabel.tsx";
import type { ApiTokenWithType } from "@/modules/access/token/apiTokenApi.ts";
import { accessCreateLink } from "@/modules/api/resourceCreateLinks.ts";
import { cn } from "@/utils/classNames.ts";
import { Trans, useLingui } from "@lingui/react/macro";
import { datetimeToDate } from "@warrenio/api-spec/conversion";
import { useAtomValue } from "jotai/react";
import type { ReactNode } from "react";

type AccessItemWithType = ApiTokenWithType | AccessDelegationWithType;

const sortFields = [
    {
        id: "name",
        title: <Trans>Name</Trans>,
        compare: localeCompareBy((i) => (i.$type === "api_token" ? i.description : i.name)),
    },
    { id: "created_at", title: <Trans>Created date</Trans>, getValue: (i) => datetimeToDate(i.created_at).getTime() },
    { id: "$type", title: <Trans>Type</Trans>, getValue: (i) => i.$type },
] satisfies SimpleSortField<AccessItemWithType>[];

export default function AccessList() {
    const { t } = useLingui();
    return (
        <SidebarList
            data={(props) => <AccessData {...props} />}
            title={t`Access`}
            sortFields={sortFields}
            actionButton={{
                title: t`Create new`,
                action: accessCreateLink,
            }}
            listId="access"
        />
    );
}

function getSearchStrings(item: AccessItemWithType): string[] {
    switch (item.$type) {
        case "api_token":
            return [item.description];
        case "access_delegation":
            return [item.name, ...item.impersonations.map((i) => i.grantee_username)];
    }
}

function AccessData({ queryString, sortId, sortAsc }: SidebarListState): ReactNode {
    const query = useAtomValue(accessListQueryAtom);

    let accessItems = getFilteredItems(queryString, query.data, getSearchStrings);
    accessItems = getSortedItems(sortFields, { sortId, sortAsc }, accessItems);

    return (
        <ListResult
            items={accessItems.map((i) =>
                i.$type === "api_token" ? (
                    <TokenBlock key={i.kong_id} item={i} />
                ) : (
                    <DelegationBlock key={i.id} item={i} />
                ),
            )}
            isSearching={!!queryString}
            query={query}
            emptyItem={
                <EmptyItem icon="jp-access-icon">
                    <Trans>
                        Create a new access to enable other users or systems to perform actions with this {siteName}{" "}
                        account.
                    </Trans>
                </EmptyItem>
            }
        />
    );
}

export function TokenBlock({ item }: { item: ApiTokenWithType }) {
    const { created_at, description, restricted } = item;
    return (
        <StandardBlock link={tokenLink(item)}>
            <div className={C.StandardBlockIcon}>
                <MaskIcon className="jp-swap-icon size-1rem" />
            </div>

            <div className={cn(C.StandardBlockName, "text-ellipsis")}>
                <b className="text-ellipsis">{description}</b>
                <div className="text-ellipsis text-primary">{scopeLabel(restricted)}</div>
            </div>

            <div className={C.StandardBlockType}>
                <div className="text-success">
                    <Trans>API</Trans>
                </div>
            </div>

            <div className={C.StandardBlockInfo}>
                <div className="text-muted">
                    <Trans>Created</Trans>
                </div>
                <ShortDate date={created_at} />
            </div>
        </StandardBlock>
    );
}

export function DelegationBlock({ item }: { item: AccessDelegationWithType }) {
    const { created_at, restricted, name } = item;
    return (
        <StandardBlock link={delegationLink(item)}>
            <div className={C.StandardBlockIcon}>
                <MaskIcon className="jp-user-group-icon size-1rem" />
            </div>

            <div className={cn(C.StandardBlockName, "text-ellipsis")}>
                <b className="text-ellipsis">{name}</b>
                <div className="text-ellipsis text-primary">{scopeLabel(restricted)}</div>
            </div>

            <div className={C.StandardBlockType}>
                <div className="text-success">
                    <Trans>UI</Trans>
                </div>
            </div>

            <div className={C.StandardBlockInfo}>
                <div className="text-muted">
                    <Trans>Created</Trans>
                </div>
                <ShortDate date={created_at} />
            </div>
        </StandardBlock>
    );
}
